import { useState, useMemo, useCallback } from 'react';
import { RunningShoe } from '../types';
import { searchShoes } from '../utils/api';

interface UseGlobalSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: RunningShoe[];
  isLoading: boolean;
  error: string | null;
  isOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;
  performSearch: (q: string) => Promise<void>;
  debouncedSearch: (q: string) => void;
  clearSearch: () => void;
}

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

export function useGlobalSearch(): UseGlobalSearchReturn {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RunningShoe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Perform search API call
  const performSearch = useCallback(async (q: string) => {
    // Don't search if query is too short
    if (q.length < 2) {
      setResults([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await searchShoes(q, 10);
      if (response.success && response.data) {
        setResults(response.data);
      } else {
        setResults([]);
      }
    } catch (err: any) {
      console.error('Search error:', err);
      setError('搜索失败，请重试');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search function (300ms delay)
  const debouncedSearch = useMemo(
    () =>
      debounce((q: string) => {
        if (q.length >= 2) {
          performSearch(q);
        } else {
          setResults([]);
          setError(null);
        }
      }, 300),
    [performSearch]
  );

  // Open search dropdown
  const openSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  // Close search dropdown
  const closeSearch = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Clear search state
  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
    setIsOpen(false);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    isOpen,
    openSearch,
    closeSearch,
    performSearch,
    debouncedSearch,
    clearSearch,
  };
}
